// Threshold math for the bottom sheet's swipe-down dismiss gesture.
//
// Pulled out as a pure module (same spirit as `isTap` in src/lib/map/
// gestures.ts) so the decision logic is unit-testable without simulating
// pointer capture in jsdom. BottomSheet's DOM-level recognizer delegates here.

/** Dismiss when the sheet has been dragged past this fraction of its height. */
export const DISMISS_DISTANCE_FRACTION = 0.4;

/**
 * Dismiss on a downward flick at or above this velocity (px/ms — 0.5 px/ms
 * is 500 px/s, a deliberate downward swipe rather than a slow drag).
 */
export const FLICK_VELOCITY_PX_PER_MS = 0.5;

/**
 * A flick must still travel at least this far. Guards against dismissing on
 * a jittery micro-movement whose instantaneous velocity happens to be high.
 */
export const FLICK_MIN_OFFSET_PX = 24;

export interface SheetDismissArgs {
  /** Final downward drag distance in px (clamped to >= 0 by the caller). */
  offset: number;
  /** Rendered sheet height in px. */
  sheetHeight: number;
  /** Most recent pointer velocity in px/ms; positive = moving down. */
  velocity: number;
}

/**
 * Pure predicate: should releasing the drag at `offset` px (with the pointer
 * last moving at `velocity`) dismiss a sheet `sheetHeight` px tall?
 */
export function shouldDismissSheet({ offset, sheetHeight, velocity }: SheetDismissArgs): boolean {
  if (offset <= 0) return false;
  if (sheetHeight > 0 && offset >= sheetHeight * DISMISS_DISTANCE_FRACTION) {
    return true;
  }
  return velocity >= FLICK_VELOCITY_PX_PER_MS && offset >= FLICK_MIN_OFFSET_PX;
}
